import { Button, Space, Table, Typography } from "antd"
import ComplaintDrawer from "../../../components/Orders/ComplaintDrawer"
import { useAuthContext } from "../../../hooks/useAuthContext"
import { FileOutlined, MinusOutlined, SearchOutlined } from "@ant-design/icons"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import { orderEndDate, orderStartDate } from "../../../components/Table/orderDateDisplay"
import OrderCountDisplay from "../../../components/Table/orderCountDisplay"
import ColumnSearchFilter from "../../../components/Table/orderNumberFilter"
import { useEffect, useMemo, useState } from "react"
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import { MaterialVarianceValue } from "../../../utils/calcMaterialVariance"
import ExportCompletedOrdersButton from "./Helper/ExportCompletedOrdersButton"
import ProductionProtocolModalButton from "./Helper/ProductionProtocolModal"
import { filterOrdersByRole } from "../../../utils/filterOrdersByRole"

const { Text } = Typography

export default function CompletedOrders({ currentKey }) {
  //gets the user from auth context
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const { user, admin, semiAdmin, devSpecialist } = useAuthContext()

  const isAdmin = admin || devSpecialist || semiAdmin

  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const selectedOrder = selectedRows.length === 1 ? selectedRows[0] : null
  const exactlyOneSelected = selectedRows.length === 1

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => { setSelectedRowKeys(keys); setSelectedRows(rows) },
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
  }

  //get data
  useEffect(() => {
    if (currentKey !== 7) return
    if (!user && !admin && !semiAdmin && !devSpecialist) return // must have user

    setLoading(true)

    const ordersRef = collection(db, "orders")

    // Build query parts
    const clauses = [where("orderStatus", "==", 7)]

    // Only restrict by uid for non-admins and non-semiAdmins
    if (!admin && !semiAdmin && !devSpecialist) {
      clauses.push(where("uid", "==", user.uid))
    }

    // Always order by completedTime desc
    const qRef = query(ordersRef, ...clauses, orderBy("completedTime", "desc"))

    const unsubscribe = onSnapshot(
      qRef,
      (snapshot) => {
        const results = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))

        // Sort by completed time (descending)
        results.sort((a, b) => {
          const av = a?.completedTime?.seconds ?? -Infinity
          const bv = b?.completedTime?.seconds ?? -Infinity
          return bv - av
        })

        // Apply helper filter
        const filteredResults = filterOrdersByRole(results, { admin, semiAdmin, devSpecialist })

        setData(filteredResults)
        setLoading(false)
      },
      (err) => {
        console.error("CompletedOrders query error:", err)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [currentKey, user, admin, semiAdmin, devSpecialist])

  //client filter
  let clientFIlters =
    data &&
    data.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  let productFilters =
    data &&
    data.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  const manufacturerFilters = useMemo(() => {
    if (!data) return []
    const seen = new Set()

    // prefer order.manufacturer, fall back to client.manufacturer for older docs
    data.forEach((item) => {
      const prefix = item?.manufacturer?.invoicePrefix ?? item?.client?.manufacturer?.invoicePrefix
      if (prefix) seen.add(prefix)
    })

    return Array.from(seen)
      .sort((a, b) => String(a).localeCompare(String(b), "lv"))
      .map((v) => ({ text: v, value: v }))
  }, [data])

  const returnCorrectButton = (record) => {
    const url = record?.invoice?.invoiceUrl
    return (
      <Button
        size="small"
        icon={<FileOutlined style={{ color: url ? "#1677ff" : undefined }} />}
        type="link"
        href={url || undefined}
        target={url ? "_blank" : undefined}
        rel={url ? "noopener noreferrer" : undefined}
        disabled={!url}>
        {url ? "Show" : record?.hasInvoice ? "Invoice missing URL" : "No invoice"}
      </Button>
    )
  }

  //default columns for table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      width: 220,
      align: "left",
      sorter: (a, b) => a.justOrderNumber - b.justOrderNumber,
      render: (value, record) => OrderAndPrefix(record),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <ColumnSearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          dataIndex="orderNumber"
        />
      ),
      onFilter: (value, record) =>
        record.orderNumber.toString().trim().toLowerCase().includes(value.trim().toLowerCase()),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client?.startsWith(value),
      filterSearch: true,
      width: 200,
      render: (value) => (
        <Text
          ellipsis={{ tooltip: value }} // shows full value on hover
          style={{ maxWidth: 180, display: "inline-block", verticalAlign: "middle" }}>
          {value}
        </Text>
      ),
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "left",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      width: "25%",
      render: (value = "") => (
        <Text
          copyable={{ text: value }}
          ellipsis={{ tooltip: value }}
          style={{
            display: "block",
            width: "100%",
            verticalAlign: "middle",
          }}>
          {value}
        </Text>
      ),
    },
    {
      title: "Manuf.",
      dataIndex: ["manufacturer", "invoicePrefix"],
      align: "left",
      width: 100,
      filters: manufacturerFilters,
      filterSearch: true,
      onFilter: (value, record) => {
        const prefix =
          record?.manufacturer?.invoicePrefix ?? record?.client?.manufacturer?.invoicePrefix
        return prefix === value
      },
      render: (_, record) => {
        const prefix =
          record?.manufacturer?.invoicePrefix ?? record?.client?.manufacturer?.invoicePrefix ?? "—"
        const company = record?.manufacturer?.company ?? record?.client?.manufacturer?.company

        return (
          <Text
            ellipsis={{ tooltip: company || prefix }}
            style={{ display: "block", width: "100%" }}>
            {prefix}
          </Text>
        )
      },
    },
    {
      title: "Count",
      dataIndex: "amountCreated",
      align: "left",
      width: 100,
      render: (value, record) => <OrderCountDisplay value={value} record={record} />,
    },
    // {
    //   title: "Material",
    //   dataIndex: ["selectedProduct", "material"],
    //   align: "left",
    //   width: 150,
    // },
    // {
    //   title: "Man.",
    //   dataIndex: "user",
    //   align: "left",
    //   width: 100,
    // },
    {
      title: "Protocol",
      key: "protocol",
      width: 120,
      hidden: !isAdmin,
      render: (_, record) => (
        <ProductionProtocolModalButton order={record} buttonProps={{ size: "small" }} />
      ),
    },
    {
      title: "Variance",
      key: "variance",
      align: "left",
      width: 100,
      render: (_, record) =>
        record?.selectedProduct?.machine === "Tape" ? (
          <MinusOutlined />
        ) : (
          <MaterialVarianceValue order={record} />
        ),
    },
    {
      title: "Invoice",
      align: "center",
      width: 120,
      render: (value, record) => {
        return returnCorrectButton(record) // Add the return keyword here
      },
    },
    {
      title: "Started",
      align: "center",
      dataIndex: "created",
      key: "created",
      filterTitle: "Creation Date",
      width: 140,
      sorter: (a, b) => {
        // Check if `doneByRewinder` exists and compare seconds if it does
        if (a.created && b.created) {
          return a.created.seconds - b.created.seconds
        } else if (a.created && !b.created) {
          // a has a date, b does not - a should come first
          return -1
        } else if (!a.created && b.created) {
          // b has a date, a does not - b should come first
          return 1
        } else {
          // Neither a nor b have a date - consider them equal
          return 0
        }
      },
      render: (value, record) => orderStartDate(value, record),
    },
    {
      title: "Ended",
      align: "center",
      dataIndex: "completedTime",
      key: "completedTime",
      filterTitle: "Done date",
      width: 140,

      sorter: (a, b) => {
        // Check if `doneByRewinder` exists and compare seconds if it does
        if (a.completedTime && b.completedTime) {
          return a.completedTime.seconds - b.completedTime.seconds
        } else if (a.completedTime && !b.completedTime) {
          // a has a date, b does not - a should come first
          return -1
        } else if (!a.completedTime && b.completedTime) {
          // b has a date, a does not - b should come first
          return 1
        } else {
          // Neither a nor b have a date - consider them equal
          return 0
        }
      },
      render: (value, record) => orderEndDate(value, record),
    },
  ]

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <ComplaintDrawer
          orderData={selectedOrder}
          disabled={!exactlyOneSelected}
          buttonText="Create Complaint"
        />
        <ExportCompletedOrdersButton
          data={selectedRows.length > 0 ? selectedRows : (data || [])}
          filenamePrefix="completed_orders"
        />
      </div>

      <Table
        style={{ marginTop: "15px" }}
        size="small"
        loading={loading}
        tableLayout="fixed"
        columns={defaultColumns}
        bordered
        dataSource={data}
        rowSelection={rowSelection}
        rowKey={(record) => record.id}
        pagination={{
          defaultPageSize: 100,
          showSizeChanger: true,
          pageSizeOptions: [50, 100, 200, 500],
        }}
      />
    </>
  )
}
